import { defineStore } from "pinia";
import axios from "axios";
import { API_URL } from "@/config/api";

export const useAdminStore = defineStore("admin", {
  state: () => ({
    // ダッシュボード
    stats: null,
    // ユーザー管理
    users: [],
    // マスターデータ
    prefectures: [],
    countries: [],
    scheduleCategories: [],
    // 通知
    notifications: [],
    // お知らせ
    announcements: [],
    // 利用規約
    terms: [],
    // プライバシーポリシー
    privacyPolicies: [],
    // Q&A
    qaItems: [],
    // ローディング・エラー
    isLoading: false,
    error: null,
  }),

  actions: {
    // ---------- Stats ----------
    async fetchStats() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(`${API_URL}/admin/stats`);
        this.stats = data;
      } catch (err) {
        this.error =
          err.response?.data?.detail || "統計情報の取得に失敗しました";
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    // ---------- ユーザー管理 ----------
    async fetchUsers() {
      this.isLoading = true;
      try {
        const { data } = await axios.get(`${API_URL}/admin/users`);
        this.users = data;
      } catch (err) {
        this.error =
          err.response?.data?.detail || "ユーザー一覧の取得に失敗しました";
        throw err;
      } finally {
        this.isLoading = false;
      }
    },

    async updateUser(userCode, payload) {
      const { data } = await axios.put(
        `${API_URL}/admin/users/${userCode}`,
        payload,
      );
      await this.fetchUsers();
      return data;
    },

    async deleteUser(userCode) {
      const { data } = await axios.delete(`${API_URL}/admin/users/${userCode}`);
      await this.fetchUsers();
      return data;
    },

    // ---------- 都道府県マスター ----------
    async fetchPrefectures() {
      const { data } = await axios.get(`${API_URL}/admin/master/prefectures`);
      this.prefectures = data;
    },

    async createPrefecture(payload) {
      const { data } = await axios.post(
        `${API_URL}/admin/master/prefectures`,
        payload,
      );
      await this.fetchPrefectures();
      return data;
    },

    async updatePrefecture(no, payload) {
      const { data } = await axios.put(
        `${API_URL}/admin/master/prefectures/${no}`,
        payload,
      );
      await this.fetchPrefectures();
      return data;
    },

    async deletePrefecture(no) {
      const { data } = await axios.delete(
        `${API_URL}/admin/master/prefectures/${no}`,
      );
      await this.fetchPrefectures();
      return data;
    },

    // ---------- 国マスター ----------
    async fetchCountries() {
      const { data } = await axios.get(`${API_URL}/admin/master/countries`);
      this.countries = data;
    },

    async createCountry(payload) {
      const { data } = await axios.post(
        `${API_URL}/admin/master/countries`,
        payload,
      );
      await this.fetchCountries();
      return data;
    },

    async updateCountry(no, payload) {
      const { data } = await axios.put(
        `${API_URL}/admin/master/countries/${no}`,
        payload,
      );
      await this.fetchCountries();
      return data;
    },

    async deleteCountry(no) {
      const { data } = await axios.delete(
        `${API_URL}/admin/master/countries/${no}`,
      );
      await this.fetchCountries();
      return data;
    },

    // ---------- 通知 ----------
    async fetchNotifications() {
      const { data } = await axios.get(`${API_URL}/admin/notifications`);
      this.notifications = data;
    },

    async sendNotification(payload) {
      const { data } = await axios.post(
        `${API_URL}/admin/notifications`,
        payload,
      );
      await this.fetchNotifications();
      return data;
    },

    async deleteNotification(notificationNo) {
      const { data } = await axios.delete(
        `${API_URL}/admin/notifications/${notificationNo}`,
      );
      await this.fetchNotifications();
      return data;
    },

    // ---------- お知らせ ----------
    async fetchAnnouncements() {
      const { data } = await axios.get(`${API_URL}/admin/announcements`);
      this.announcements = data;
    },

    async createAnnouncement(payload) {
      const { data } = await axios.post(
        `${API_URL}/admin/announcements`,
        payload,
      );
      await this.fetchAnnouncements();
      return data;
    },

    async updateAnnouncement(no, payload) {
      const { data } = await axios.put(
        `${API_URL}/admin/announcements/${no}`,
        payload,
      );
      await this.fetchAnnouncements();
      return data;
    },

    async deleteAnnouncement(no) {
      const { data } = await axios.delete(
        `${API_URL}/admin/announcements/${no}`,
      );
      await this.fetchAnnouncements();
      return data;
    },

    // ---------- 利用規約 ----------
    async fetchTerms() {
      const { data } = await axios.get(`${API_URL}/admin/terms`);
      this.terms = data;
    },

    async createTerms(payload) {
      const { data } = await axios.post(`${API_URL}/admin/terms`, payload);
      await this.fetchTerms();
      return data;
    },

    async updateTerms(version, payload) {
      const { data } = await axios.put(
        `${API_URL}/admin/terms/${version}`,
        payload,
      );
      await this.fetchTerms();
      return data;
    },

    async deleteTerms(version) {
      const { data } = await axios.delete(`${API_URL}/admin/terms/${version}`);
      await this.fetchTerms();
      return data;
    },

    // ---------- プライバシーポリシー ----------
    async fetchPrivacyPolicies() {
      const { data } = await axios.get(`${API_URL}/admin/privacy`);
      this.privacyPolicies = data;
    },

    async createPrivacyPolicy(payload) {
      const { data } = await axios.post(`${API_URL}/admin/privacy`, payload);
      await this.fetchPrivacyPolicies();
      return data;
    },

    async updatePrivacyPolicy(version, payload) {
      const { data } = await axios.put(
        `${API_URL}/admin/privacy/${version}`,
        payload,
      );
      await this.fetchPrivacyPolicies();
      return data;
    },

    async deletePrivacyPolicy(version) {
      const { data } = await axios.delete(
        `${API_URL}/admin/privacy/${version}`,
      );
      await this.fetchPrivacyPolicies();
      return data;
    },

    // ---------- Q&A ----------
    async fetchQaItems() {
      const { data } = await axios.get(`${API_URL}/admin/qa`);
      this.qaItems = data;
    },

    async createQaItem(payload) {
      const { data } = await axios.post(`${API_URL}/admin/qa`, payload);
      await this.fetchQaItems();
      return data;
    },

    async updateQaItem(qaNo, payload) {
      const { data } = await axios.put(`${API_URL}/admin/qa/${qaNo}`, payload);
      await this.fetchQaItems();
      return data;
    },

    async deleteQaItem(qaNo) {
      const { data } = await axios.delete(`${API_URL}/admin/qa/${qaNo}`);
      await this.fetchQaItems();
      return data;
    },

    // ---------- スケジュールカテゴリマスター ----------
    async fetchScheduleCategories() {
      const { data } = await axios.get(
        `${API_URL}/admin/master/schedule-categories`,
      );
      this.scheduleCategories = data;
    },

    async createScheduleCategory(payload) {
      const { data } = await axios.post(
        `${API_URL}/admin/master/schedule-categories`,
        payload,
      );
      await this.fetchScheduleCategories();
      return data;
    },

    async updateScheduleCategory(categoryNo, payload) {
      const { data } = await axios.put(
        `${API_URL}/admin/master/schedule-categories/${categoryNo}`,
        payload,
      );
      await this.fetchScheduleCategories();
      return data;
    },

    async deleteScheduleCategory(categoryNo) {
      const { data } = await axios.delete(
        `${API_URL}/admin/master/schedule-categories/${categoryNo}`,
      );
      await this.fetchScheduleCategories();
      return data;
    },
  },
});
